import { useState, useCallback } from "react";
import { CustomButton } from "@/components/ui/Buttons";
import FileUploadField from "@/components/ui/FileUploadField";
import RadioButton from "@/components/ui/RadioButton";
import { FileDetails } from "@/types/types";
import { useReportProductShippingIssueMutation } from "@/redux/feature/orderManagement/productShipping/productShippingApi";
import useOrderVideoParams from "@/hooks/useOrderVideoParams";
import { handleApiRequest } from "@/utils/handleApiRequest";
import toast from "react-hot-toast";
import DatePicker from "react-datepicker";
import CalenderIcon from "@/assets/icons/CalenderGray.svg";

interface HelpModalProps {
  onClose: (isOpen: boolean) => void;
  isCreatorBuyingProduct?: boolean;
  expected_delivery_date?:Date,
}
type Value = Date | null | undefined;

const HelpModal = ({
  expected_delivery_date,
  onClose,
  isCreatorBuyingProduct = false,
}: HelpModalProps) => {
  // State management
  const [selectedReason, setSelectedReason] = useState<string>("");
  const [willShipItem, setWillShipItem] = useState<boolean | null>(null);
  const [details, setDetails] = useState<string>("");
  const [damageImages, setDamageImages] = useState<FileDetails | null>(null);
  const [loading, setIsLoading] = useState(false);
  const { orderId, videoId } = useOrderVideoParams();
  const [selectedDate, setSelectedDate] = useState<Value>(null);
  const [delayInDeliveryFile, setDelayInDeliveryFile] =
    useState<FileDetails | null>(null);

  const [reportIssue] = useReportProductShippingIssueMutation();

  // console.log(damageImages);

  // Event handlers
  const handleSubmit = useCallback(async () => {
    const payload = {
      order_id: orderId,
      video_id: videoId,
      data: {
        reason: selectedReason,
        delayed_delivery_date: selectedDate,
        delayInDeliveryFile:
          selectedReason === "delay_in_delivery" ? delayInDeliveryFile : null,
        will_ship_to_another_creator:
          selectedReason === "not_available" ? willShipItem : null,
        details: selectedReason === "others" ? details : "",
        evidence:
          selectedReason === "shipping_damage" ||
          selectedReason === "mistake_damage"
            ? damageImages
            : [],
      },
    };


    setIsLoading(true);
    await handleApiRequest({
      payload,
      requestFunction: reportIssue,
      onSuccess: () => {
        toast.success("Reported");
        setIsLoading(false);
        onClose(false);
      },
      onError: (err) => {
        toast.error(err.data.message || "Something went wrong");
        setIsLoading(false);
      },
    });
  }, [
    selectedReason,
    willShipItem,
    details,
    damageImages,
    selectedDate,
    delayInDeliveryFile,
  ]);

  const handleCancel = useCallback(() => {
    setSelectedReason("");
    setWillShipItem(null);
    setDetails("");
    setDamageImages(null);
    onClose(false);
  }, [onClose]);

  // Validation for submit button
  const isSubmitDisabled = useCallback(() => {
    if (loading) return true;
    if (!selectedReason) return true;
    if (selectedReason === "delay_in_delivery" && selectedDate === null)
      return true;

    if (selectedReason === "not_available" && willShipItem === null)
      return true;
    if (selectedReason === "others" && !details.trim()) return true;
    if (
      (selectedReason === "shipping_damage" ||
        selectedReason === "mistake_damage") &&
      damageImages === null
    )
      return true;
    return false;
  }, [selectedReason, willShipItem, details, damageImages, selectedDate]);

  // console.log(isCreatorBuyingProduct);

  // Conditional rendering for shipping damage section
  const renderShippingDamageSection = () => (
    <div className="space-y-2">
      <label className="text-[#344054] text-sm leading-5 font-medium">
        Please share the images of the damaged product
      </label>
      <div className="border border-dashed border-[#B6BAC3] rounded-lg">
        <FileUploadField
          maxFiles={1}
          accept={{
            "image/png": [],
            "image/jpeg": [],
            "application/pdf": [],
            "video/mp4": [],
            "image/gif": [],
          }}
          onFilesUploaded={(files) => setDamageImages(files[0])}
        />
      </div>
    </div>
  );
  const renderDelayInDeliveryDetailsField = () => (
    <div className="space-y-2 pl-4">
      {/* Expected Delivery Date */}
      <div className="px-[12px]  flex items-center w-fit border border-[#D0D5DD] rounded-[10px]">
        <p className="text-[#363636] py-[10px] text-sm font-medium pr-[12px]">
          Expected Date
        </p>
        <button className="text-[#959FB2] leading-[15px] flex items-center py-[6px] border-[#D0D5DD] px-[12px] text-sm border-l">
          <DatePicker
            selected={selectedDate}
            onChange={(date) => setSelectedDate(date)}
            dateFormat="dd/MM/yyyy"
            minDate={new Date(expected_delivery_date as Date)}
            placeholderText="Select a Date"
            className="w-[125px]  text-sm  font-medium text-gray-700 cursor-pointer pl-4 focus:outline-none"
            popperPlacement="bottom-start"
          />
          <CalenderIcon />
        </button>
      </div>
      <div className="mt-4">
        <p className="text-[#344054] text-sm font-medium leading-5 mb-2 mt-4">
          Please provide documentation supporting the delay.
        </p>
        <div className="rounded-[10px]">
          <FileUploadField
            onFilesUploaded={(file) => setDelayInDeliveryFile(file[0])}
            maxFiles={1}
          />
        </div>
      </div>
    </div>
  );

  // Conditional rendering for others section
  const renderOthersSection = () => (
    <input
      type="text"
      value={details}
      onChange={(e) => setDetails(e.target.value)}
      placeholder="Please specify details"
      className="text-[#959FB2] text-sm leading-5 tracking-[-0.5%] font-medium px-4 py-3 border border-[#C2CCD6] rounded-lg w-full outline-[#17457D]"
    />
  );

  return (
    <div className="space-y-6 max-h-[640px]">
      {/* Header Section */}
      <div className="space-y-2">
        <h5 className="text-base font-semibold text-gray-900 tracking-tight">
          How can we help you?
        </h5>
        <p className="text-xs text-gray-500">
          Briefly describe the reason for reporting
        </p>
      </div>

      {/* Reason Selection Section */}
      <div className="space-y-3">
        <label className="text-sm font-medium text-gray-700">
          Select the reason for reporting
        </label>
        <div className="space-y-3">
          {/* Delay in Delivery*/}
          {!isCreatorBuyingProduct && (
            <div className="flex items-center gap-x-2">
              <RadioButton
                checked={selectedReason === "delay_in_delivery"}
                onChange={() => setSelectedReason("delay_in_delivery")}
              />
              <span className="text-sm text-gray-600">Delay in Delivery</span>
            </div>
          )}
          {/* Conditional sections */}
          {selectedReason === "delay_in_delivery" &&
            renderDelayInDeliveryDetailsField()}

          {/* Not available for Shoot */}
          <div className="space-y-3">
            <div className="flex items-center gap-x-2">
              <RadioButton
                checked={selectedReason === "not_available"}
                onChange={() => setSelectedReason("not_available")}
              />
              <span className="text-sm text-gray-600">
                Not available for Shoot
              </span>
            </div>
            {selectedReason === "not_available" && (
              <div className="ml-7 space-y-2">
                <p className="text-[#505050] text-[13px]">
                  Are you willing to ship the item to the next creator?
                </p>
                <div className="flex gap-x-8">
                  <div className="flex items-center gap-x-2">
                    <RadioButton
                      checked={willShipItem === true}
                      onChange={() => setWillShipItem(true)}
                    />
                    <span className="text-sm text-gray-600">Yes</span>
                  </div>
                  <div className="flex items-center gap-x-2">
                    <RadioButton
                      checked={willShipItem === false}
                      onChange={() => setWillShipItem(false)}
                    />
                    <span className="text-sm text-gray-600">No</span>
                  </div>
                </div>
                <p className="text-[12px] text-[#B54708] font-medium leading-4 border border-[#FBEEBF] bg-[#FFFAEB] rounded-[16px] px-2.5 py-[2px] whitespace-nowrap">
                  Note - The product is required to be in original condition.
                </p>
              </div>
            )}
          </div>

          {/* Shipping Damage*/}
          {!isCreatorBuyingProduct && (
            <div className="flex items-center gap-x-2">
              <RadioButton
                checked={selectedReason === "shipping_damage"}
                onChange={() => setSelectedReason("shipping_damage")}
              />
              <span className="text-sm text-gray-600">
                Item damaged in shipping
              </span>
            </div>
          )}

          {/* Mistake Damage */}
          {!isCreatorBuyingProduct && (
            <div className="flex items-center gap-x-2">
              <RadioButton
                checked={selectedReason === "mistake_damage"}
                onChange={() => setSelectedReason("mistake_damage")}
              />
              <span className="text-sm text-gray-600">
                Item damaged by mistake
              </span>
            </div>
          )}

          <div className="flex items-center gap-x-2">
            <RadioButton
              checked={selectedReason === "others"}
              onChange={() => setSelectedReason("others")}
            />
            <span className="text-sm text-gray-600">Others</span>
          </div>
        </div>
      </div>

      {(selectedReason === "shipping_damage" ||
        selectedReason === "mistake_damage") &&
        renderShippingDamageSection()}
      {selectedReason === "others" && renderOthersSection()}

      {/* Action Buttons */}
      <div className="flex gap-x-3 mt-6">
        <CustomButton
          variant="outline"
          color="#344054"
          fontSize="16px"
          fontWeight="600"
          border="1px solid #D0D5DD"
          borderRadius="8px"
          padding="10px"
          className="flex-1"
          onClick={handleCancel}
        >
          Cancel
        </CustomButton>
        <CustomButton
          backgroundColor="#17457D"
          fontSize="16px"
          fontWeight="600"
          border="1px solid #17457D"
          borderRadius="8px"
          padding="10px"
          className="flex-1"
          onClick={handleSubmit}
          disabled={isSubmitDisabled()}
        >
          {loading ? "Reporting.." : "Report"}
        </CustomButton>
      </div>
    </div>
  );
};

export default HelpModal;
